import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  CssBaseline,
  IconButton,
  Modal,
  Toolbar,
  Typography,
  styled,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RefreshIcon from "@mui/icons-material/Refresh";
import CloseIcon from "@mui/icons-material/Close";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { axiosClient } from "../../AxiosClient";
import { fetchMediaUrl } from "../../chatCobranza/ChatCobranza";
import MessageThread from "./MessageThread";

const BRAND = "#161e55";

const ButtonSecondary = styled(Button)({
  borderRadius: "20px",
  color: "#fff",
  borderColor: "#fff",
  textTransform: "none",
});

const HeaderOffset = styled(Box)({
  height: "32px",
  flexShrink: 0,
});

/** Nombre legible del cliente, con guardas contra campos ausentes. */
const buildClientName = (conversation, fallbackName, documentNumber) => {
  const name = [conversation?.name, conversation?.lastName]
    .map((part) => (part != null ? String(part).trim() : ""))
    .filter(Boolean)
    .join(" ")
    .trim();
  return name || fallbackName || documentNumber || "Cliente";
};

const messagesAreaSx = {
  flex: 1,
  minHeight: 0,
  overflowY: "auto",
  backgroundColor: "#efeae2",
  backgroundImage:
    'url("https://user-images.githubusercontent.com/15075759/28719144-86dc0f70-73b1-11e7-911d-60d70fcded21.png")',
  backgroundSize: "contain",
  py: 2,
};

export default function ConversacionV4() {
  const { kind: kindParam, identifier } = useParams();
  const kind = kindParam === "comercio" ? "comercio" : "cliente";
  const navigate = useNavigate();
  const { state } = useLocation();

  const [conversation, setConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [media, setMedia] = useState(null); // { type: "image"|"video", url }

  const messagesEndRef = useRef(null);
  const shouldScrollRef = useRef(true);

  const loadConversation = useCallback(async () => {
    if (!identifier) {
      setLoading(false);
      setError(
        kind === "comercio"
          ? "Sin número de celular del comercio."
          : "Sin número de documento del cliente."
      );
      setConversation(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const basePath =
        kind === "comercio"
          ? "/v1/conversations/commerce/"
          : "/v1/conversations/document/";
      const { data } = await axiosClient.get(
        `${basePath}${encodeURIComponent(identifier)}`
      );
      setConversation(data);
      shouldScrollRef.current = true;
    } catch (e) {
      setError(
        e?.response?.data?.message ||
          e?.message ||
          "No se pudo cargar la conversación."
      );
      setConversation(null);
    } finally {
      setLoading(false);
    }
  }, [identifier, kind]);

  useEffect(() => {
    loadConversation();
  }, [loadConversation]);

  // Auto-scroll al fondo cuando llegan/cambian los mensajes (solo tras una carga).
  useLayoutEffect(() => {
    if (shouldScrollRef.current && conversation?.messages?.length) {
      requestAnimationFrame(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "auto", block: "end" });
        shouldScrollRef.current = false;
      });
    }
  }, [conversation?.messages]);

  // Resuelve la URL firmada al abrir el modal de imagen (la clave S3 llega cruda).
  const openImage = useCallback((key) => {
    setMedia({ type: "image", url: null });
    fetchMediaUrl(key, (url) => setMedia({ type: "image", url }));
  }, []);

  const handleMediaClick = useCallback(
    (src, mediaType) => {
      if (mediaType === "image") {
        openImage(src);
      } else {
        // El componente Video entrega una URL ya resuelta.
        setMedia({ type: "video", url: src });
      }
    },
    [openImage]
  );

  const isCommerce = kind === "comercio";
  const subjectName = isCommerce
    ? conversation?.name || state?.commerceName || identifier || "Comercio"
    : buildClientName(conversation, state?.nameClient, identifier);
  const subjectPhone =
    conversation?.cellphone ||
    (isCommerce ? state?.cellphoneCommerce : state?.cellphoneClient) ||
    "";
  const subjectLabel = isCommerce
    ? "Conversación del comercio"
    : "Conversación del cliente";

  const isOutgoing = useCallback(
    (message) =>
      Boolean(conversation?.cellphone) &&
      message?.senderCellphone !== conversation.cellphone,
    [conversation?.cellphone]
  );

  return (
    <React.Fragment>
      <CssBaseline />
      <Box
        sx={{
          width: "100%",
          minWidth: 0,
          height: "100vh",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Toolbar />
        <HeaderOffset />

        {/* Encabezado estilo WhatsApp con marca Prami */}
        <Box
          sx={{
            backgroundColor: BRAND,
            color: "#fff",
            px: 2,
            py: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              {subjectName}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.85 }}>
              {subjectPhone ? `${subjectPhone} · ` : ""}
              {subjectLabel}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
            <ButtonSecondary
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadConversation}
              disabled={loading}
            >
              Actualizar
            </ButtonSecondary>
            <ButtonSecondary
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
            >
              Volver
            </ButtonSecondary>
          </Box>
        </Box>

        {error ? (
          <Alert severity="error" sx={{ m: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        ) : null}

        <Box sx={messagesAreaSx}>
          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
              }}
            >
              <CircularProgress sx={{ color: BRAND }} />
            </Box>
          ) : (
            <MessageThread
              messages={conversation?.messages}
              isOutgoing={isOutgoing}
              fetchMediaUrl={fetchMediaUrl}
              onMediaClick={handleMediaClick}
            />
          )}
          <div ref={messagesEndRef} />
        </Box>
      </Box>

      {/* Modal simple para ampliar imagen/video */}
      <Modal open={Boolean(media)} onClose={() => setMedia(null)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            maxWidth: "90vw",
            maxHeight: "90vh",
            outline: "none",
          }}
        >
          <IconButton
            onClick={() => setMedia(null)}
            sx={{
              position: "absolute",
              top: -40,
              right: 0,
              color: "#fff",
            }}
          >
            <CloseIcon />
          </IconButton>
          {media?.type === "image" && media?.url ? (
            <img
              src={media.url}
              alt="Adjunto de la conversación"
              style={{ maxWidth: "90vw", maxHeight: "90vh", display: "block" }}
            />
          ) : media?.type === "video" && media?.url ? (
            <video
              src={media.url}
              controls
              autoPlay
              style={{ maxWidth: "90vw", maxHeight: "90vh", display: "block" }}
            />
          ) : (
            <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
              <CircularProgress sx={{ color: "#fff" }} />
            </Box>
          )}
        </Box>
      </Modal>
    </React.Fragment>
  );
}
